/*
 * This file is part of glowingblue/orell-thalia-common.
 *
 * Copyright (c) Glowing Blue AG.
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import app from 'flarum/forum/app';
import Page from 'flarum/common/components/Page';
import type { IPageAttrs } from 'flarum/common/components/Page';
import ItemList from 'flarum/common/utils/ItemList';
import listItems from 'flarum/common/helpers/listItems';
import Button from 'flarum/common/components/Button';
import LogInModal from 'flarum/forum/components/LogInModal';
import extractText from 'flarum/common/utils/extractText';
import type Mithril from 'mithril';
import type * as fofTerms from '@fof-terms';
import HomeWelcome from './HomeWelcome';
import HomeDiscussions from './HomeDiscussions';
import HomeCategories from './HomeCategories';
import HomeActivityLeaderboard from './HomeActivityLeaderboard';

export default class HomePage extends Page {
	oninit(vnode: Mithril.Vnode<IPageAttrs, this>) {
		super.oninit(vnode);

		app.history.push('home', extractText(app.translator.trans('core.forum.header.back_to_index_tooltip')));
		this.bodyClass = 'App--home';
		app.setTitle(app.translator.trans('core.forum.index.meta_title_text') as string);
	}

	view() {
		let UpdateAlert: typeof fofTerms.components.UpdateAlert | undefined;
		if (app.initializers.has('fof-terms')) {
			UpdateAlert = (flarum.extensions['fof-terms'] as unknown as typeof fofTerms).components.UpdateAlert;
		}

		const groups = this.blockGroups().toArray() as ItemList<Mithril.Children>[];

		return (
			<div className="HomePage">
				{/* Add a login to the header for guests on mobile devices */}
				{!app.session.user && (
					<Button
						className="Button Button--flat Button--icon HomeLoginButton"
						icon="fas fa-user force-fa-style"
						onclick={() => app.modal.show(LogInModal)}
					/>
				)}
				{UpdateAlert && <UpdateAlert />}
				<div className="container HomePage-blocks_wrapper">
					<ul className="HomePage-block_groups">
						{groups.map((blocks: any) => (
							<li className={`item-${blocks.itemName}`}>
								<ul className="HomePage-blocks">
									{listItems(blocks.toArray())}
									{blocks.toArray().length % 2 > 0 && (
										<div className="HomePage-empty_block">
											{/* Empty item, used to fix alignment of the last (not empty) item if there is an odd number of items */}
										</div>
									)}
								</ul>
							</li>
						))}
					</ul>
				</div>
			</div>
		);
	}

	blockGroups(): ItemList<ItemList<Mithril.Children>> {
		const groups = new ItemList<ItemList<Mithril.Children>>();

		groups.add('welcome', this.welcomeBlocks(), 100);
		groups.add('latest', this.latestBlocks(), 90);
		groups.add('categories', new ItemList<Mithril.Children>().add('categories', <HomeCategories />), 70);
		groups.add('leaderboards', this.leaderboardBlocks(), 50);

		return groups;
	}

	welcomeBlocks(): ItemList<Mithril.Children> {
		const blocks = new ItemList<Mithril.Children>();

		blocks.add('welcome', <HomeWelcome />, 100);

		return blocks;
	}

	latestBlocks(): ItemList<Mithril.Children> {
		const blocks = new ItemList<Mithril.Children>();

		blocks.add(
			'discussions',
			<HomeDiscussions
				title={[
					<span className="BCQuote opening" />,
					<span>{app.translator.trans('glowingblue-orell-thalia-common.forum.home.discussions.title')}</span>,
				]}
				params={{ filter: { of_home: true, '-isReview': true } }}
				showBtn={true}
			/>,
			100
		);

		return blocks;
	}

	leaderboardBlocks(): ItemList<Mithril.Children> {
		const blocks = new ItemList<Mithril.Children>();
		blocks.add('posts-leaderboard', <HomeActivityLeaderboard />, 10);
		return blocks;
	}
}
